import { UnreconciledFundsStatusId } from 'app/api/apiClient/generated/types';
import { SemanticBillState } from 'app/api/apiClient/settlement/types';
import { NotificationProps } from 'app/components/v2/components/Notification';
import { StatusColor } from 'app/components/v2/components/Pill/utils';

import { CreditNoteStatusId } from './creditNotes';
import { InvoiceStatusId } from './invoices';
import { LoadStatusId } from './loads';
import { NoaStatusId } from './noa';
import { PayeeRelationshipStatusId } from './payee-relationships';
import { PayoutStatusId } from './payment';
import { PayorInvoiceBatchReviewStatus } from './payorInvoiceBatchReview';

// Local Typings
interface StatusConfig {
  isError: boolean;
  pillColor: StatusColor;
  severity: NotificationProps['severity'];
}
interface StatusConfigWithStatusNote extends StatusConfig {
  shouldDisplayStatusNote: boolean;
}

// Local Variables
const errorConfig: StatusConfig = {
  isError: true,
  pillColor: 'red',
  severity: 'error',
};
const infoConfig: StatusConfig = {
  isError: false,
  pillColor: 'blue',
  severity: 'info',
};
const neutralConfig: StatusConfig = {
  isError: false,
  pillColor: 'blue',
  severity: 'info',
};
const successConfig: StatusConfig = {
  isError: false,
  pillColor: 'green',
  severity: 'success',
};
const warningConfig: StatusConfig = {
  isError: false,
  pillColor: 'yellow',
  severity: 'warning',
};

export const invoiceStatusConfig: Record<InvoiceStatusId, StatusConfigWithStatusNote | undefined> = {
  [InvoiceStatusId.Approved]: {
    ...successConfig,
    shouldDisplayStatusNote: false,
  },
  [InvoiceStatusId.Held]: {
    ...errorConfig,
    shouldDisplayStatusNote: true,
  },
  [InvoiceStatusId.MissingBank]: {
    ...errorConfig,
    shouldDisplayStatusNote: false,
  },
  [InvoiceStatusId.MissingCarrier]: {
    ...errorConfig,
    shouldDisplayStatusNote: false,
  },
  [InvoiceStatusId.Paid]: {
    ...successConfig,
    shouldDisplayStatusNote: false,
  },
  [InvoiceStatusId.PendingReissue]: {
    ...errorConfig,
    shouldDisplayStatusNote: true,
  },
  [InvoiceStatusId.Pending]: {
    ...warningConfig,
    shouldDisplayStatusNote: true,
  },
  [InvoiceStatusId.Removed]: {
    ...infoConfig,
    shouldDisplayStatusNote: true,
  },
};

export const creditNoteStatusConfig: Record<CreditNoteStatusId, StatusConfigWithStatusNote | undefined> = {
  [CreditNoteStatusId.AppliedInFull]: {
    ...successConfig,
    shouldDisplayStatusNote: false,
  },
  [CreditNoteStatusId.Held]: {
    ...errorConfig,
    shouldDisplayStatusNote: true,
  },
  [CreditNoteStatusId.ImportError]: {
    ...errorConfig,
    shouldDisplayStatusNote: false,
  },
  [CreditNoteStatusId.Outstanding]: {
    ...infoConfig,
    shouldDisplayStatusNote: false,
  },
  [CreditNoteStatusId.Pending]: {
    ...warningConfig,
    shouldDisplayStatusNote: false,
  },
  [CreditNoteStatusId.Removed]: {
    ...neutralConfig,
    shouldDisplayStatusNote: false,
  },
};

export const relationshipStatusConfig: Record<PayeeRelationshipStatusId, StatusConfigWithStatusNote | undefined> = {
  [PayeeRelationshipStatusId.Connected]: {
    ...successConfig,
    shouldDisplayStatusNote: false,
  },
  [PayeeRelationshipStatusId.Held]: {
    ...errorConfig,
    shouldDisplayStatusNote: true,
  },
  [PayeeRelationshipStatusId.FactorLocked]: {
    ...warningConfig,
    shouldDisplayStatusNote: false,
  },
  [PayeeRelationshipStatusId.Factored]: {
    ...warningConfig,
    shouldDisplayStatusNote: false,
  },
  [PayeeRelationshipStatusId.Unregistered]: {
    ...warningConfig,
    shouldDisplayStatusNote: false,
  },
};

export const noaStatusColors: Record<NoaStatusId, StatusColor | undefined> = {
  [NoaStatusId.ActionConflict]: 'red',
  [NoaStatusId.Complete]: 'green',
  [NoaStatusId.ConversionErrorNotified]: 'red',
  [NoaStatusId.ConversionError]: 'red',
  [NoaStatusId.Converted]: 'blue',
  [NoaStatusId.DeferredAndNotified]: 'yellow',
  [NoaStatusId.Deferred]: 'yellow',
  [NoaStatusId.Deprecated]: 'red',
  [NoaStatusId.FraudConfirmed]: 'green',
  [NoaStatusId.FraudInvestigation]: 'red',
  [NoaStatusId.Held]: 'blue',
  [NoaStatusId.PendingLorDesignationHighRisk]: 'red',
  [NoaStatusId.PendingLorDesignation]: 'yellow',
  [NoaStatusId.PendingLorValidation]: 'yellow',
  [NoaStatusId.PendingNewFactor]: 'yellow',
  [NoaStatusId.PendingOcr]: 'yellow',
  [NoaStatusId.ReadyToAudit]: 'blue',
  [NoaStatusId.ReadyToConvert]: 'blue',
  [NoaStatusId.ReadyToProcess]: 'blue',
  [NoaStatusId.Removed]: 'red',
  [NoaStatusId.RequestToSplit]: 'green',
  [NoaStatusId.Uploaded]: 'blue',
};
export const loadStatusColors: Record<LoadStatusId, StatusColor | undefined> = {
  [LoadStatusId.Booked]: infoConfig.pillColor,
  [LoadStatusId.Delivered]: successConfig.pillColor,
  [LoadStatusId.InTransit]: infoConfig.pillColor,
  [LoadStatusId.Issue]: errorConfig.pillColor,
  [LoadStatusId.Pending]: warningConfig.pillColor,
  [LoadStatusId.Removed]: errorConfig.pillColor,
};

export const paymentStatusColors: Record<PayoutStatusId, StatusColor | undefined> = {
  [PayoutStatusId.Initializing]: infoConfig.pillColor,
  [PayoutStatusId.Paid]: successConfig.pillColor,
  [PayoutStatusId.Pending]: warningConfig.pillColor,
  [PayoutStatusId.Removed]: neutralConfig.pillColor,
};

export const payorPaperworkStatusColors: Record<SemanticBillState, StatusColor> = {
  approved: 'green',
  delivered: 'blue',
  exception: 'red',
  ignored: 'blue',
  incomplete: 'yellow',
  pending: 'yellow',
  rejected: 'red',
  removed: 'red',
  sent: 'yellow',
  submitted: 'blue',
};

export const payeePaperworkStatusConfig: Record<SemanticBillState, StatusConfigWithStatusNote | undefined> = {
  approved: {
    ...successConfig,
    shouldDisplayStatusNote: false,
  },
  delivered: {
    ...infoConfig,
    shouldDisplayStatusNote: false,
  },
  exception: {
    ...errorConfig,
    shouldDisplayStatusNote: true,
  },
  ignored: {
    ...neutralConfig,
    shouldDisplayStatusNote: false,
  },
  incomplete: {
    ...warningConfig,
    shouldDisplayStatusNote: false,
  },
  pending: {
    ...warningConfig,
    shouldDisplayStatusNote: false,
  },
  rejected: {
    ...errorConfig,
    shouldDisplayStatusNote: true,
  },
  removed: {
    ...neutralConfig,
    shouldDisplayStatusNote: false,
  },
  sent: {
    ...infoConfig,
    shouldDisplayStatusNote: false,
  },
  submitted: {
    ...infoConfig,
    shouldDisplayStatusNote: false,
  },
};

export const payorInvoiceReviewStatusConfig: Record<PayorInvoiceBatchReviewStatus, StatusConfig | undefined> = {
  [PayorInvoiceBatchReviewStatus.Issue]: errorConfig,
  [PayorInvoiceBatchReviewStatus.PendingReview]: warningConfig,
  [PayorInvoiceBatchReviewStatus.Released]: successConfig,
};

export const unreconciledFundsStatusColors: Record<UnreconciledFundsStatusId, StatusColor> = {
  [UnreconciledFundsStatusId.Reversed]: 'green',
  [UnreconciledFundsStatusId.Unreconciled]: 'yellow',
};
